import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CalendarInterview } from './Calendar.type';
import { getPersonInterviewsThunk } from './Calendar.thunk';

type CalendarState = {
  interviewsByPersonId: Record<number, CalendarInterview[]>;
  loadingByPersonId: Record<number, boolean>;
};

type CalendarRootState = {
  calendar: CalendarState;
};

const initialState: CalendarState = {
  interviewsByPersonId: {},
  loadingByPersonId: {},
};

const EMPTY_INTERVIEWS: CalendarInterview[] = [];

export const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    resetPersonInterviewsAction: (state, action: PayloadAction<number>) => {
      delete state.interviewsByPersonId[action.payload];
      delete state.loadingByPersonId[action.payload];
    },
    upsertInterviewLocalAction: (
      state,
      action: PayloadAction<{
        personId: number;
        interview: Partial<CalendarInterview> & {
          id?: number;
          googleEventId?: string;
        };
      }>,
    ) => {
      const { personId, interview } = action.payload;
      const list = state.interviewsByPersonId[personId] || [];
      const index = list.findIndex(
        item =>
          (interview.id != null && item.id === interview.id) ||
          (!!interview.googleEventId &&
            item.googleEventId === interview.googleEventId),
      );

      if (index >= 0) {
        state.interviewsByPersonId[personId] = list.map((item, i) =>
          i === index ? { ...item, ...interview } : item,
        );
        return;
      }

      if (
        interview.id != null &&
        interview.personRecordId != null &&
        interview.googleEventId &&
        interview.title &&
        interview.startTime &&
        interview.endTime
      ) {
        state.interviewsByPersonId[personId] = [
          ...list,
          interview as CalendarInterview,
        ];
      }
    },
    removeInterviewLocalAction: (
      state,
      action: PayloadAction<{ personId: number; interviewId: number }>,
    ) => {
      const { personId, interviewId } = action.payload;
      const list = state.interviewsByPersonId[personId] || [];
      state.interviewsByPersonId[personId] = list.filter(
        item => item.id !== interviewId,
      );
    },
  },
  extraReducers: builder => {
    builder.addCase(getPersonInterviewsThunk.pending, (state, action) => {
      state.loadingByPersonId[action.meta.arg] = true;
    });
    builder.addCase(getPersonInterviewsThunk.fulfilled, (state, action) => {
      const { personId, interviews } = action.payload;
      state.interviewsByPersonId[personId] = interviews;
      state.loadingByPersonId[personId] = false;
    });
    builder.addCase(getPersonInterviewsThunk.rejected, (state, action) => {
      state.loadingByPersonId[action.meta.arg] = false;
    });
  },
});

export const {
  resetPersonInterviewsAction,
  upsertInterviewLocalAction,
  removeInterviewLocalAction,
} = calendarSlice.actions;

export const selectPersonInterviews =
  (personId: number) => (state: CalendarRootState) =>
    state.calendar.interviewsByPersonId[personId] || EMPTY_INTERVIEWS;

export const selectPersonInterviewsLoading =
  (personId: number) => (state: CalendarRootState) =>
    !!state.calendar.loadingByPersonId[personId];

export const selectPersonInterviewsFetched =
  (personId: number) => (state: CalendarRootState) =>
    Object.prototype.hasOwnProperty.call(
      state.calendar.interviewsByPersonId,
      personId,
    );

export default calendarSlice.reducer;
